import { useState } from 'react';
import { Link } from 'react-router-dom';

const WA_NUMBER = '919873385555';

const tabs = [
  { key: 'ryzen', label: 'Ryzen', color: '#f97316', glow: 'rgba(249,115,22,0.35)' },
  { key: 'intel', label: 'Intel Gold', color: '#3b82f6', glow: 'rgba(59,130,246,0.35)' },
  { key: 'epyc', label: 'AMD EPYC', color: '#ef4444', glow: 'rgba(239,68,68,0.35)' },
];

const plans = {
  ryzen: [
    {
      name: 'Ryzen S',
      cpu: 'AMD Ryzen 5 5600X (6C/12T)',
      ram: '64GB DDR4 ECC',
      storage: '2×1TB NVMe',
      network: '1 Gbps',
      price: '₹7,999/mo',
      accent: '#f97316',
    },
    {
      name: 'Ryzen M',
      cpu: 'AMD Ryzen 7 5800X (8C/16T)',
      ram: '128GB DDR4 ECC',
      storage: '2×2TB NVMe RAID-1',
      network: '1 Gbps',
      price: '₹11,999/mo',
      accent: '#f97316',
    },
    {
      name: 'Ryzen XL',
      cpu: 'AMD Ryzen 9 5950X (16C/32T)',
      ram: '256GB DDR4 ECC',
      storage: '4×2TB NVMe RAID-10',
      network: '10 Gbps',
      price: '₹18,999/mo',
      accent: '#f97316',
      popular: true,
    },
  ],
  intel: [
    {
      name: 'Intel Gold S',
      cpu: 'Intel Xeon Gold 5218 (16C/32T, 2.3GHz)',
      ram: '128GB DDR4 ECC',
      storage: '2×2TB NVMe',
      network: '1 Gbps',
      price: '₹14,999/mo',
      accent: '#3b82f6',
    },
    {
      name: 'Intel Gold M',
      cpu: 'Dual Intel Xeon Gold 6226R (32C/64T, 2.9GHz)',
      ram: '256GB DDR4 ECC',
      storage: '4×2TB NVMe RAID-10',
      network: '10 Gbps',
      price: '₹28,999/mo',
      accent: '#3b82f6',
      popular: true,
    },
    {
      name: 'Intel Gold XL',
      cpu: 'Dual Intel Xeon Gold 6330 (56C/112T, 2.0GHz)',
      ram: '512GB DDR4 ECC',
      storage: '4×3.84TB NVMe RAID',
      network: '10 Gbps',
      price: '₹49,999/mo',
      accent: '#3b82f6',
    },
  ],
  epyc: [
    {
      name: 'EPYC S',
      cpu: 'AMD EPYC 7302P (16C/32T, 3.0GHz)',
      ram: '128GB DDR4 ECC',
      storage: '2×2TB NVMe',
      network: '1 Gbps',
      price: '₹15,999/mo',
      accent: '#ef4444',
    },
    {
      name: 'EPYC M',
      cpu: 'AMD EPYC 7513 (32C/64T, 2.6GHz)',
      ram: '256GB DDR4 ECC',
      storage: '4×2TB NVMe RAID-10',
      network: '10 Gbps',
      price: '₹29,999/mo',
      accent: '#ef4444',
      popular: true,
    },
    {
      name: 'EPYC XL',
      cpu: 'AMD EPYC 7713 (64C/128T, 2.0GHz)',
      ram: '512GB DDR4 ECC',
      storage: '4×3.84TB NVMe RAID',
      network: '25 Gbps',
      price: '₹54,999/mo',
      accent: '#ef4444',
    },
    {
      name: 'EPYC Max',
      cpu: 'Dual AMD EPYC 9654 (192C/384T)',
      ram: '1TB DDR5 ECC',
      storage: '8×3.84TB NVMe RAID',
      network: '25 Gbps',
      price: '₹1,09,999/mo',
      accent: '#ef4444',
    },
  ],
};

const included = [
  'Full Root Access',
  'IPMI / iDRAC Console',
  'Hardware RAID Controller',
  'DDoS Protection',
  '99.9% Uptime SLA',
  'Mumbai DC (Tier 3)',
];

const specIcons = {
  cpu: '🖥️',
  ram: '💾',
  storage: '💿',
  network: '🌐',
};

function SpecRow({ icon, label, value }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '8px 0',
      borderBottom: '1px solid rgba(255,255,255,0.06)',
    }}>
      <span style={{ fontSize: '16px' }}>{icon}</span>
      <span style={{ color: '#8b8fa8', fontSize: '13px', minWidth: '68px' }}>{label}</span>
      <span style={{ color: '#e2e8f0', fontSize: '13px', fontWeight: '500', marginLeft: 'auto', textAlign: 'right' }}>{value}</span>
    </div>
  );
}

function PlanCard({ plan }) {
  const [hovered, setHovered] = useState(false);

  const waMsg = encodeURIComponent(`Hi, I'm interested in the ${plan.name} Bare Metal Server plan at ${plan.price}. Please share more details.`);
  const waLink = `https://wa.me/${WA_NUMBER}?text=${waMsg}`;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered
          ? 'linear-gradient(135deg, #0d0e18 0%, #111220 100%)'
          : 'linear-gradient(135deg, #0a0b14 0%, #0d0e18 100%)',
        border: `1px solid ${hovered ? plan.accent : 'rgba(255,255,255,0.08)'}`,
        borderRadius: '16px',
        padding: '28px 24px',
        flex: '1 1 260px',
        minWidth: '240px',
        maxWidth: '320px',
        position: 'relative',
        transition: 'all 0.3s ease',
        boxShadow: hovered ? `0 8px 40px ${plan.accent}22` : '0 2px 16px rgba(0,0,0,0.4)',
        cursor: 'default',
      }}
    >
      {plan.popular && (
        <div style={{
          position: 'absolute',
          top: '-12px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: `linear-gradient(90deg, ${plan.accent}, ${plan.accent}cc)`,
          color: '#fff',
          fontSize: '11px',
          fontWeight: '700',
          padding: '4px 14px',
          borderRadius: '20px',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}>
          ★ Most Popular
        </div>
      )}

      <div style={{
        display: 'inline-block',
        background: `${plan.accent}18`,
        border: `1px solid ${plan.accent}44`,
        color: plan.accent,
        fontSize: '12px',
        fontWeight: '700',
        padding: '3px 10px',
        borderRadius: '6px',
        marginBottom: '14px',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
      }}>
        {plan.name}
      </div>

      <div style={{ margin: '0 0 20px 0' }}>
        <SpecRow icon={specIcons.cpu} label="CPU" value={plan.cpu} />
        <SpecRow icon={specIcons.ram} label="RAM" value={plan.ram} />
        <SpecRow icon={specIcons.storage} label="Storage" value={plan.storage} />
        <SpecRow icon={specIcons.network} label="Network" value={plan.network} />
      </div>

      <div style={{
        fontSize: '28px',
        fontWeight: '800',
        color: plan.accent,
        marginBottom: '20px',
        letterSpacing: '-0.5px',
      }}>
        {plan.price}
      </div>

      <a
        href={waLink}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          background: 'linear-gradient(135deg, #25D366, #128C7E)',
          color: '#fff',
          fontWeight: '700',
          fontSize: '14px',
          padding: '11px 0',
          borderRadius: '10px',
          textDecoration: 'none',
          transition: 'opacity 0.2s ease',
          width: '100%',
        }}
        onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
        onMouseLeave={e => e.currentTarget.style.opacity = '1'}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
          <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.118 1.528 5.852L0 24l6.317-1.508A11.933 11.933 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.021-1.374l-.36-.214-3.748.894.928-3.657-.234-.376A9.818 9.818 0 1112 21.818z"/>
        </svg>
        Get on WhatsApp
      </a>
    </div>
  );
}

export default function BaremetalServers() {
  const [activeTab, setActiveTab] = useState('ryzen');
  const currentTab = tabs.find(t => t.key === activeTab);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#030305',
      color: '#e2e8f0',
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
      paddingBottom: '80px',
    }}>
      {/* Hero */}
      <div style={{
        background: 'linear-gradient(180deg, #08090f 0%, #030305 100%)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        padding: '72px 24px 56px',
        textAlign: 'center',
      }}>
        <div style={{
          display: 'inline-block',
          background: 'rgba(0,212,255,0.1)',
          border: '1px solid rgba(0,212,255,0.25)',
          color: '#00d4ff',
          fontSize: '12px',
          fontWeight: '700',
          padding: '5px 14px',
          borderRadius: '20px',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          marginBottom: '20px',
        }}>
          Mumbai DC · Tier 3 · Instant Deploy
        </div>
        <h1 style={{
          fontSize: 'clamp(32px, 5vw, 56px)',
          fontWeight: '800',
          margin: '0 0 16px',
          lineHeight: 1.1,
          background: 'linear-gradient(135deg, #ffffff 0%, #a0aec0 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>
          Bare Metal Servers
        </h1>
        <p style={{
          fontSize: '18px',
          color: '#8b8fa8',
          maxWidth: '560px',
          margin: '0 auto 0',
          lineHeight: 1.6,
        }}>
          Dedicated hardware, zero noisy neighbours. Enterprise-grade servers deployed in Mumbai with full root access.
        </p>
      </div>

      {/* Included with all plans */}
      <div style={{
        maxWidth: '1100px',
        margin: '40px auto 0',
        padding: '0 24px',
      }}>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          justifyContent: 'center',
          marginBottom: '48px',
        }}>
          {included.map(item => (
            <div key={item} style={{
              background: 'rgba(0,212,255,0.05)',
              border: '1px solid rgba(0,212,255,0.15)',
              color: '#00d4ff',
              fontSize: '13px',
              fontWeight: '500',
              padding: '6px 14px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              <span style={{ color: '#22c55e' }}>✓</span> {item}
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '8px',
          justifyContent: 'center',
          marginBottom: '40px',
          flexWrap: 'wrap',
        }}>
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '12px 28px',
                borderRadius: '10px',
                border: activeTab === tab.key
                  ? `2px solid ${tab.color}`
                  : '2px solid rgba(255,255,255,0.1)',
                background: activeTab === tab.key
                  ? `${tab.color}18`
                  : 'rgba(255,255,255,0.03)',
                color: activeTab === tab.key ? tab.color : '#8b8fa8',
                fontSize: '15px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.25s ease',
                boxShadow: activeTab === tab.key ? `0 0 20px ${tab.glow}` : 'none',
                letterSpacing: '0.02em',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab label strip */}
        <div style={{
          textAlign: 'center',
          marginBottom: '36px',
        }}>
          <span style={{
            fontSize: '13px',
            color: '#8b8fa8',
            background: 'rgba(255,255,255,0.04)',
            padding: '4px 14px',
            borderRadius: '6px',
          }}>
            Showing <span style={{ color: currentTab.color, fontWeight: '700' }}>{currentTab.label}</span> plans
          </span>
        </div>

        {/* Plan Cards */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '20px',
          justifyContent: 'center',
        }}>
          {plans[activeTab].map(plan => (
            <PlanCard key={plan.name} plan={plan} />
          ))}
        </div>

        {/* Custom / Contact CTA */}
        <div style={{
          marginTop: '56px',
          padding: '36px 32px',
          background: 'linear-gradient(135deg, #0a0b14 0%, #0f1020 100%)',
          border: '1px solid rgba(0,212,255,0.15)',
          borderRadius: '20px',
          textAlign: 'center',
        }}>
          <h3 style={{
            fontSize: '22px',
            fontWeight: '700',
            color: '#fff',
            margin: '0 0 10px',
          }}>
            Need a Custom Configuration?
          </h3>
          <p style={{
            color: '#8b8fa8',
            fontSize: '15px',
            margin: '0 0 24px',
            lineHeight: 1.6,
          }}>
            Multi-server setups, custom RAM, NVMe configs, colocation — we handle it all. Talk to our team.
          </p>
          <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent('Hi, I need a custom bare metal server configuration. Can you help?')}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'linear-gradient(135deg, #25D366, #128C7E)',
                color: '#fff',
                fontWeight: '700',
                fontSize: '15px',
                padding: '13px 28px',
                borderRadius: '12px',
                textDecoration: 'none',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.118 1.528 5.852L0 24l6.317-1.508A11.933 11.933 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.021-1.374l-.36-.214-3.748.894.928-3.657-.234-.376A9.818 9.818 0 1112 21.818z"/>
              </svg>
              Chat on WhatsApp
            </a>
            <Link
              to="/contact"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(0,212,255,0.08)',
                border: '1px solid rgba(0,212,255,0.3)',
                color: '#00d4ff',
                fontWeight: '700',
                fontSize: '15px',
                padding: '13px 28px',
                borderRadius: '12px',
                textDecoration: 'none',
              }}
            >
              Contact Sales →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
